import { useCallback, useState } from "react";
import {
  DebitNoteEntity,
  DebitNoteListStatus,
  GetAllDebitNotesUseCase,
  PaginationInfo,
} from "../../domain";
import { DebitNoteRepositoryImpl } from "../../data/repositories/DebitNoteRepositoryImpl";

const debitNoteRepository = new DebitNoteRepositoryImpl();
const getAllDebitNotesUseCase = new GetAllDebitNotesUseCase(debitNoteRepository);

export interface DebitNoteListParams {
  page?: number;
  limit?: number;
  status?: DebitNoteListStatus;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: "created_at" | "updated_at" | "document_number" | "status";
  sortOrder?: "asc" | "desc";
}

export const useDebitNoteList = () => {
  const [debitNotes, setDebitNotes] = useState<DebitNoteEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDebitNotes = useCallback(async (params: DebitNoteListParams = {}) => {
    setLoading(true);
    setError(null);

    const limit = params.limit ?? 10;
    const page = params.page ?? 1;

    try {
      const result = await getAllDebitNotesUseCase.execute({
        limit,
        offset: Math.max(page - 1, 0) * limit,
        status: params.status ?? "all",
        search: params.search?.trim() || undefined,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        sort_by: params.sortBy ?? "created_at",
        sort_order: params.sortOrder ?? "desc",
      });

      setDebitNotes(result.debitNotes);
      setPagination(result.pagination);
    } catch (error: any) {
      setError(error.message || "Failed to load debit notes");
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    debitNotes,
    pagination,
    loading,
    error,
    fetchDebitNotes,
    clearError,
  };
};
